import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader,
  Edit2,
  X,
  Check,
  Archive,
  ArchiveRestore,
  RotateCcw,
  User as UserIcon,
  Ruler,
  Factory,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import {
  CreateOpportunityParams,
  UpdateOpportunityParams,
  HydratedOpportunity,
  OpportunityStage,
  OpportunityStatus,
  DEFAULT_OPPORTUNITY_PROBABILITY,
  IN_PROGRESS_STATUSES,
  MAX_OPPORTUNITY_PROBABILITY,
  MIN_OPPORTUNITY_PROBABILITY,
  canEditOwnedEntity,
  isOpportunityOwner,
  Permission,
} from "@forgepoint/shared";
import { useOpportunityStagesStore } from "./opportunityStagesStore";
import { useOpportunitiesStore } from "./opportunitiesStore";
import {
  useAuthStore,
  useHasAnyPermission,
  useHasPermission,
} from "@/store/authStore";
import { useOpportunitySettingsStore } from "@/store/opportunitySettingsStore";
import {
  getUserDisplayName,
  useTeamsStore,
  useUserDisplayNames,
} from "@/store/teamsStore";
import { OpportunityActivityLog } from "./activity/OpportunityActivityLog";
import {
  OpportunityEditorSheet,
  OpportunityFormData,
} from "./OpportunityEditorSheet";
import { CompanySelector } from "@/components/CompanySelector";
import { CurrencyInput } from "@/components/CurrencyInputDecimal";
import { UserSelector } from "@/components/UserSelector";
import { PeopleSelector } from "@/components/PeopleSelector";
import { usePeopleStore } from "@/modules/people/peopleStore";
import { LocationSelector } from "@/components/LocationSelector";
import { PeopleDisplay } from "./PeopleDisplay";
import { OpportunityQuotesSection } from "./OpportunityQuotesSection";
import { OpportunityThreadsSection } from "./OpportunityThreadsSection";
import { OpportunityTasksSection } from "./OpportunityTasksSection";
import { StandardAgentThreadView } from "@/modules/standardAgent/components/StandardAgentThreadView";
import { TASK_ID_PARAM } from "@/modules/standardAgent/useTaskDeepLink";
import { CopyLinkButton } from "@/components/CopyLinkButton";
import { NotesSection } from "@/components/notes/NotesSection";
import { OpportunityTimingBar } from "./components/OpportunityTimingBar";
import { OpportunityStageStepper } from "./components/OpportunityStageStepper";
import { CustomFieldsSection } from "@/components/customFields/CustomFieldsSection";
import { OpportunityDemandForecastSection } from "@/modules/demandForecasts/OpportunityDemandForecastSection";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import Decimal from "decimal.js";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import isEqual from "fast-deep-equal";
import { useStageRequirements } from "./useStageRequirements";
import {
  focusStageField,
  getFieldPlaceholder,
  getStageSaveLabel,
  type StageRequiredField,
} from "./stageRequirements";
import { StageReadiness } from "./components/StageReadiness";
import { RequiredMark } from "./components/RequiredMark";

const SHEET_OPEN_ANIMATION_MS = 500;
const TAB_SWITCH_FOCUS_DELAY_MS = 50;
const DEFAULT_TAB = "tasks";
const CUSTOM_FIELDS_TAB = "custom-fields";

/**
 * Returns the initial form data for creating a new opportunity.
 * @param defaultProbability - Organization's default probability setting
 * @param defaultStageId - Default stage ID for new opportunities
 * @returns Initial form data with defaults applied
 */
const getInitialFormData = (
  defaultProbability: number,
  defaultStageId: string | null,
): OpportunityFormData => ({
  name: "",
  companyId: null,
  salesRepUserId: null,
  estimatorUserIds: [],
  personIds: [],
  locationId: null,
  amount: "0.00",
  status: "INTAKE",
  stageId: defaultStageId,
  probability: defaultProbability,
  customFields: {},
});

/**
 * Maps a hydrated opportunity into form data shape.
 * @param opportunity - The opportunity to convert
 * @returns Form data populated from the opportunity
 */
const getFormDataFromOpportunity = (
  opportunity: HydratedOpportunity,
): OpportunityFormData => ({
  name: opportunity.name,
  companyId: opportunity.companyId,
  salesRepUserId: opportunity.salesRepUserId ?? null,
  estimatorUserIds: opportunity.estimatorUserIds ?? [],
  personIds: (opportunity.people ?? []).map((person) => person.id),
  locationId: opportunity.locationId ?? null,
  amount: opportunity.amount,
  status: opportunity.status,
  stageId: opportunity.stageId ?? null,
  probability: opportunity.probability,
  customFields: opportunity.customFields ?? {},
});

/**
 * Selects a stage on the form, carrying over its category and the probability it implies.
 * @param formData - The current form data
 * @param stage - The stage to select
 * @returns The form data with the stage selected
 */
const withStageSelected = (
  formData: OpportunityFormData,
  stage: OpportunityStage,
): OpportunityFormData => {
  const probabilityByCategory: Partial<Record<OpportunityStatus, number>> = {
    ACCEPTED: MAX_OPPORTUNITY_PROBABILITY,
    LOST: MIN_OPPORTUNITY_PROBABILITY,
  };
  return {
    ...formData,
    stageId: stage.id,
    ...(stage.category ? { status: stage.category } : {}),
    probability:
      (stage.category ? probabilityByCategory[stage.category] : undefined) ??
      stage.probability ??
      formData.probability,
  };
};

type OpportunityDetailSheetProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      opportunityId?: undefined;
      onSave: (data: CreateOpportunityParams) => Promise<true | string>;
      onArchive?: undefined;
      onUnarchive?: undefined;
      mode: "create";
      enableThreadDeepLink?: boolean;
      readOnly?: undefined;
      initialEditStageId?: undefined;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      opportunityId: string;
      onSave: (data: UpdateOpportunityParams) => Promise<true | string>;
      onArchive?: (id: string) => Promise<void>;
      onUnarchive?: (id: string) => Promise<void>;
      mode: "edit";
      enableThreadDeepLink?: boolean;
      /** When true, hides the Edit/archive actions so the sheet stays read-only. */
      readOnly?: boolean;
      /** Opens straight into edit mode with this stage selected. */
      initialEditStageId?: string;
    };

/**
 * Displays the sales rep name with a user icon for the read-only detail view.
 * @param salesRepUserId - The ID of the sales rep user, or null if unassigned
 */
function SalesRepDisplay({
  salesRepUserId,
}: {
  salesRepUserId: string | null;
}) {
  const users = useTeamsStore((state) => state.users);

  if (!salesRepUserId) {
    return null;
  }

  const user = users.find((u) => u.id === salesRepUserId);
  if (!user) {
    return null;
  }

  const name = getUserDisplayName(user);

  return (
    <div className="flex items-center gap-1.5 mt-1 mb-3">
      <UserIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-[11px] font-medium uppercase tracking-wide leading-none text-muted-foreground/70">
        Sales
      </span>
      <p className="text-sm text-muted-foreground">{name}</p>
    </div>
  );
}

/**
 * Displays the assigned estimators with a user icon for the read-only detail view.
 * @param estimatorUserIds - The IDs of the estimator users
 */
function EstimatorsDisplay({
  estimatorUserIds,
}: {
  estimatorUserIds: string[];
}) {
  const displayNames = useUserDisplayNames();

  const names = estimatorUserIds
    .map((id) => displayNames.get(id))
    .filter((name): name is string => name !== undefined);

  if (names.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 mt-1 mb-3">
      <Ruler className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-[11px] font-medium uppercase tracking-wide leading-none text-muted-foreground/70">
        {names.length > 1 ? "Estimators" : "Estimator"}
      </span>
      <p className="text-sm text-muted-foreground">{names.join(", ")}</p>
    </div>
  );
}

/**
 * Displays the company name with an icon for the read-only detail view.
 * @param companyName - The company name, or null if no company is linked
 */
function CompanyDisplay({ companyName }: { companyName: string | null }) {
  if (!companyName) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 mt-1 mb-3">
      <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-[11px] font-medium uppercase tracking-wide leading-none text-muted-foreground/70">
        Company
      </span>
      <p className="text-sm text-muted-foreground">{companyName}</p>
    </div>
  );
}

/**
 * Displays the location in the read-only detail view.
 * @param locationName - The location name, or null if no location is selected
 */
function LocationDisplay({ locationName }: { locationName: string | null }) {
  if (!locationName) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5 mt-1 mb-3">
      <Factory className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <p className="text-sm text-muted-foreground">{locationName}</p>
    </div>
  );
}

/**
 * A sheet component that displays opportunity details, linked quotes, and activity history.
 * Shows a read-only view by default with an Edit button.
 * In edit mode, displays the form fields for editing opportunity details.
 * Displays linked quotes with ability to link/unlink quotes to the opportunity.
 * In create mode, only shows the form (no linked quotes or activity exists yet for a new opportunity).
 * Fetches opportunity data and quotes internally when opened in edit mode and pulls data from the store to stay in sync.
 * Supports creating, updating, archiving, and unarchiving opportunities through callback props.
 * Linked quotes are clickable and navigate to the quotes page with the selected quote opened.
 * @param enableThreadDeepLink - When true (default), reads/writes the open linked thread via the `threadId` URL param, and its open task via `taskId`. Pass false when rendered inside a thread view to avoid recursively re-opening the same thread.
 * @param readOnly - When true, hides the Edit/archive actions so the sheet stays in its read-only view.
 * @param initialEditStageId - When set, opens in edit mode with this stage selected and focuses its first missing required field.
 */
export const OpportunityDetailSheet: React.FC<OpportunityDetailSheetProps> = ({
  open,
  onOpenChange,
  opportunityId,
  onSave,
  onArchive,
  onUnarchive,
  mode,
  enableThreadDeepLink = true,
  readOnly = false,
  initialEditStageId,
}) => {
  const demandForecastingEnabled = useFeatureFlag("demand-forecasting");
  const { getOpportunity } = useOpportunitiesStore();
  const { getActiveStages, getStage, getDefaultStage } =
    useOpportunityStagesStore();
  const stagesHasFetched = useOpportunityStagesStore((s) => s.hasFetched);
  const fetchStages = useOpportunityStagesStore((s) => s.fetchStages);

  const storeOpportunity = useOpportunitiesStore((state) =>
    open && mode === "edit" && opportunityId
      ? state.getHydratedOpportunity(opportunityId)
      : undefined,
  );
  const opportunityLoadingState = useOpportunitiesStore((state) =>
    open && mode === "edit" && opportunityId
      ? state.getOpportunityLoadingState(opportunityId)
      : null,
  );

  const isLoading = opportunityLoadingState?.isLoadingGet ?? false;
  const fetchError = opportunityLoadingState?.getError ?? null;
  const isArchiving = opportunityLoadingState?.isLoadingArchive ?? false;
  const canEditAllOpportunities = useHasPermission(
    Permission.OPPORTUNITIES_EDIT_ALL,
  );
  const canEditOwnOpportunities = useHasPermission(
    Permission.OPPORTUNITIES_EDIT_OWN,
  );
  const canViewPeople = useHasAnyPermission(
    Permission.PEOPLE_VIEW_ALL,
    Permission.PEOPLE_VIEW_OWN,
  );
  const currentUserId = useAuthStore((state) => state.user?.id);
  const canEditOpportunity = canEditOwnedEntity(
    canEditAllOpportunities,
    canEditOwnOpportunities,
    isOpportunityOwner(currentUserId, {
      salesRepUserId: storeOpportunity?.salesRepUserId,
      estimatorUserIds: storeOpportunity?.estimatorUserIds,
    }),
  );
  const isReadOnly = readOnly || !canEditOpportunity;

  const orgDefaultProbability = useOpportunitySettingsStore(
    (s) => s.settings?.defaultProbability ?? DEFAULT_OPPORTUNITY_PROBABILITY,
  );
  const customFieldsSchema = useOpportunitySettingsStore(
    (s) => s.settings?.customFieldsSchema ?? null,
  );
  const fetchOpportunitySettings = useOpportunitySettingsStore(
    (s) => s.fetchSettings,
  );
  const upsertPeople = usePeopleStore((s) => s.upsertPeople);
  const quoteSettings = useQuoteSettingsStore((s) => s.settings);
  const fetchQuoteSettings = useQuoteSettingsStore((s) => s.fetchSettings);

  useEffect(() => {
    fetchOpportunitySettings();
  }, [fetchOpportunitySettings]);

  useEffect(() => {
    if (!quoteSettings) {
      fetchQuoteSettings();
    }
  }, [quoteSettings, fetchQuoteSettings]);

  useEffect(() => {
    if (!stagesHasFetched) {
      fetchStages();
    }
  }, [stagesHasFetched, fetchStages]);

  const [formData, setFormData] = useState<OpportunityFormData>(
    getInitialFormData(orgDefaultProbability, getDefaultStage()?.id ?? null),
  );
  const lastSyncedForm = useRef<{
    id: string;
    form: OpportunityFormData;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(
    mode === "create" || Boolean(initialEditStageId),
  );
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const seededStageIdRef = useRef<string | null>(null);
  const [shouldFocusFirstMissing, setShouldFocusFirstMissing] = useState(false);
  const [fieldToFocus, setFieldToFocus] = useState<{
    field: StageRequiredField;
    delayMs: number;
  } | null>(null);
  const stageRequirements = useStageRequirements(
    formData.stageId,
    formData,
    customFieldsSchema,
  );
  const showLocationField =
    (quoteSettings?.showLocationField ?? false) ||
    stageRequirements.isStandardRequired("locationId");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const threadIdFromUrl =
    enableThreadDeepLink && mode === "edit"
      ? searchParams.get("threadId")
      : null;

  /**
   * Writes the open linked thread to the URL, dropping any task param from the previous thread.
   * @param threadId - The thread to open, or null to clear it
   * @returns Nothing
   */
  const setThreadIdQueryParam = useCallback(
    (threadId: string | null) => {
      if (!enableThreadDeepLink) {
        return;
      }
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(TASK_ID_PARAM);
      if (threadId) {
        nextSearchParams.set("threadId", threadId);
      } else {
        nextSearchParams.delete("threadId");
      }
      setSearchParams(nextSearchParams);
    },
    [enableThreadDeepLink, searchParams, setSearchParams],
  );

  const handleThreadOpen = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      setThreadIdQueryParam(threadId);
    },
    [setThreadIdQueryParam],
  );

  const handleThreadClose = useCallback(() => {
    setSelectedThreadId(null);
    setThreadIdQueryParam(null);
  }, [setThreadIdQueryParam]);

  useEffect(() => {
    if (!open || mode !== "edit") {
      return;
    }
    setSelectedThreadId(threadIdFromUrl);
  }, [open, mode, threadIdFromUrl]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === "edit" && opportunityId) {
      getOpportunity(opportunityId);
      setIsEditing(false);
      setActiveTab(DEFAULT_TAB);
    } else if (mode === "create") {
      setFormData(
        getInitialFormData(
          orgDefaultProbability,
          getDefaultStage()?.id ?? null,
        ),
      );
      setIsEditing(true);
    }
  }, [
    open,
    mode,
    opportunityId,
    getOpportunity,
    orgDefaultProbability,
    getDefaultStage,
  ]);

  useEffect(() => {
    if (!open || mode !== "edit") {
      lastSyncedForm.current = null;
      return;
    }
    if (storeOpportunity && !isLoading) {
      upsertPeople(storeOpportunity.people ?? []);
      const nextForm = getFormDataFromOpportunity(storeOpportunity);
      const previous = lastSyncedForm.current;
      lastSyncedForm.current = { id: storeOpportunity.id, form: nextForm };
      setFormData((current) =>
        previous?.id === storeOpportunity.id && !isEqual(current, previous.form)
          ? current
          : nextForm,
      );
    }
  }, [open, mode, storeOpportunity, isLoading, upsertPeople]);

  useEffect(() => {
    if (!open || !initialEditStageId || !storeOpportunity || isLoading) {
      return;
    }
    if (seededStageIdRef.current === initialEditStageId) {
      return;
    }
    const stage = getStage(initialEditStageId);
    if (!stage) {
      return;
    }
    seededStageIdRef.current = initialEditStageId;
    setFormData((current) => withStageSelected(current, stage));
    setIsEditing(true);
    setShouldFocusFirstMissing(true);
  }, [open, initialEditStageId, storeOpportunity, isLoading, getStage]);

  useEffect(() => {
    if (!shouldFocusFirstMissing) {
      return;
    }
    setShouldFocusFirstMissing(false);
    const firstMissingField = stageRequirements.missingFields[0];
    if (firstMissingField) {
      setFieldToFocus({
        field: firstMissingField,
        delayMs: SHEET_OPEN_ANIMATION_MS,
      });
    }
  }, [shouldFocusFirstMissing, stageRequirements.missingFields]);

  useEffect(() => {
    if (!fieldToFocus || isLoading) {
      return;
    }
    if (fieldToFocus.field.kind === "custom") {
      setActiveTab(CUSTOM_FIELDS_TAB);
    }
    const timeout = setTimeout(() => {
      focusStageField(fieldToFocus.field);
      setFieldToFocus(null);
    }, fieldToFocus.delayMs);
    return () => clearTimeout(timeout);
  }, [fieldToFocus, isLoading]);

  /**
   * Jumps to a required field from the readiness summary.
   * @param field - The required field to focus
   * @returns Nothing
   */
  const handleSelectRequiredField = (field: StageRequiredField) => {
    setFieldToFocus({ field, delayMs: TAB_SWITCH_FOCUS_DELAY_MS });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFormData(
        getInitialFormData(
          orgDefaultProbability,
          getDefaultStage()?.id ?? null,
        ),
      );
      setIsEditing(false);
      setSelectedThreadId(null);
      seededStageIdRef.current = null;
    }
    onOpenChange(newOpen);
  };

  const handleSave = async (): Promise<true | string> => {
    if (!formData.name.trim()) {
      return "Opportunity name is required";
    }

    if (!formData.amount || formData.amount === "") {
      return "Amount is required";
    }

    setIsSaving(true);
    try {
      if (mode === "create") {
        const saveData: CreateOpportunityParams = {
          name: formData.name.trim(),
          companyId: formData.companyId || undefined,
          amount: formData.amount,
          status: formData.status,
          stageId: formData.stageId ?? undefined,
          salesRepUserId: formData.salesRepUserId,
          estimatorUserIds: formData.estimatorUserIds,
          personIds: formData.personIds,
          ...(showLocationField ? { locationId: formData.locationId } : {}),
          probability: formData.probability,
          customFields: formData.customFields,
        };
        const result = await (
          onSave as (data: CreateOpportunityParams) => Promise<true | string>
        )(saveData);
        if (result === true) {
          handleOpenChange(false);
        }
        return result;
      } else {
        const saveData: UpdateOpportunityParams = {
          name: formData.name.trim(),
          companyId: formData.companyId || undefined,
          amount: formData.amount,
          status: formData.status,
          stageId: formData.stageId ?? undefined,
          salesRepUserId: formData.salesRepUserId,
          estimatorUserIds: formData.estimatorUserIds,
          personIds: formData.personIds,
          ...(showLocationField ? { locationId: formData.locationId } : {}),
          probability: formData.probability,
          customFields: formData.customFields,
        };
        const result = await (
          onSave as (data: UpdateOpportunityParams) => Promise<true | string>
        )(saveData);
        if (result === true) {
          setIsEditing(false);
        }
        return result;
      }
    } catch {
      return mode === "create"
        ? "Failed to create opportunity"
        : "Failed to update opportunity";
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Calls handleSave and shows a toast based on the result.
   * Used by the inline edit Save button in the detail view.
   */
  const handleSaveWithToast = async () => {
    const result = await handleSave();
    if (result === true) {
      toast.success(
        mode === "create"
          ? "Opportunity created successfully"
          : "Opportunity updated successfully",
      );
    } else {
      toast.error(result);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (mode === "create") {
      handleOpenChange(false);
    } else {
      if (storeOpportunity) {
        setFormData(getFormDataFromOpportunity(storeOpportunity));
      }
      setIsEditing(false);
    }
  };

  const handleArchiveClick = () => {
    setShowArchiveDialog(true);
  };

  const handleArchiveConfirm = async () => {
    if (mode !== "edit" || !opportunityId || !onArchive) {
      return;
    }

    setShowArchiveDialog(false);
    handleOpenChange(false);
    await onArchive(opportunityId);
  };

  const handleUnarchiveClick = async () => {
    if (mode !== "edit" || !opportunityId || !onUnarchive) {
      return;
    }

    handleOpenChange(false);
    await onUnarchive(opportunityId);
  };

  const renderDetailView = () => {
    if (!storeOpportunity && mode !== "create") {
      return null;
    }

    const displayOpportunity = storeOpportunity;
    const hasCustomFields = Boolean(
      customFieldsSchema &&
        Object.keys(customFieldsSchema.properties).length > 0,
    );
    const tabCount = hasCustomFields ? 4 : 3;
    const activeStages = getActiveStages();
    const currentStageId = displayOpportunity?.stageId ?? formData.stageId;

    const showProbability = displayOpportunity
      ? IN_PROGRESS_STATUSES.includes(displayOpportunity.status)
      : IN_PROGRESS_STATUSES.includes(formData.status);
    const isArchived = Boolean(displayOpportunity?.archivedAt);

    const formattedAmount = displayOpportunity
      ? new Decimal(displayOpportunity.amount)
          .toNumber()
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
      : null;
    const savedCustomFields = displayOpportunity?.customFields ?? {};
    const customFieldsDirty = !isEqual(
      formData.customFields,
      savedCustomFields,
    );
    const saveLabel = getStageSaveLabel(
      displayOpportunity?.stageId ?? null,
      stageRequirements.stage,
      stageRequirements.missingFields.length,
    );
    const handleResetCustomFields = () => {
      setFormData({
        ...formData,
        customFields: savedCustomFields,
      });
    };

    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="@container/opportunity-header space-y-3">
                <div className="flex flex-col gap-2 @[480px]/opportunity-header:flex-row @[480px]/opportunity-header:flex-wrap @[480px]/opportunity-header:items-end">
                  <div className="@[480px]/opportunity-header:min-w-48 @[480px]/opportunity-header:flex-1">
                    <Label
                      htmlFor="name"
                      className="text-sm text-muted-foreground"
                    >
                      Opportunity Name
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Enter opportunity name"
                      className="text-base"
                    />
                  </div>
                  <div className="flex max-w-full flex-col-reverse gap-2 @[480px]/opportunity-header:flex-row">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveWithToast}
                      disabled={isSaving}
                      className="h-auto min-h-8 whitespace-normal py-1.5 text-left"
                    >
                      {isSaving ? (
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      {saveLabel}
                    </Button>
                  </div>
                </div>
                <div
                  className={stageRequirements.getStandardFieldClassName(
                    "companyId",
                  )}
                  data-stage-field="companyId"
                >
                  <Label
                    htmlFor="company"
                    className="text-sm text-muted-foreground"
                  >
                    Company
                    {stageRequirements.isStandardRequired("companyId") && (
                      <RequiredMark />
                    )}
                  </Label>
                  <CompanySelector
                    currentlySelectedId={formData.companyId}
                    onSelectId={(companyId: string | null) =>
                      setFormData({
                        ...formData,
                        companyId,
                      })
                    }
                    placeholder={getFieldPlaceholder(
                      "Select a company",
                      stageRequirements.isStandardRequired("companyId"),
                    )}
                  />
                </div>
                <div
                  className={stageRequirements.getStandardFieldClassName(
                    "salesRepUserId",
                  )}
                  data-stage-field="salesRepUserId"
                >
                  <Label
                    htmlFor="salesRep"
                    className="text-sm text-muted-foreground"
                  >
                    Sales Rep
                    {stageRequirements.isStandardRequired("salesRepUserId") && (
                      <RequiredMark />
                    )}
                  </Label>
                  <UserSelector
                    currentlySelectedId={formData.salesRepUserId}
                    onSelectId={(salesRepUserId: string | null) =>
                      setFormData({ ...formData, salesRepUserId })
                    }
                    placeholder={getFieldPlaceholder(
                      "Select a sales rep",
                      stageRequirements.isStandardRequired("salesRepUserId"),
                    )}
                    size="sm"
                  />
                </div>
                <div
                  className={stageRequirements.getStandardFieldClassName(
                    "estimatorUserIds",
                  )}
                  data-stage-field="estimatorUserIds"
                >
                  <Label
                    htmlFor="estimators"
                    className="text-sm text-muted-foreground"
                  >
                    Estimators
                    {stageRequirements.isStandardRequired(
                      "estimatorUserIds",
                    ) && <RequiredMark />}
                  </Label>
                  <UserSelector
                    mode="multi"
                    selectedUserIds={formData.estimatorUserIds}
                    onUserIdsChange={(estimatorUserIds: string[]) =>
                      setFormData({ ...formData, estimatorUserIds })
                    }
                    placeholder={getFieldPlaceholder(
                      "Select estimators",
                      stageRequirements.isStandardRequired("estimatorUserIds"),
                    )}
                    size="sm"
                  />
                </div>
                {canViewPeople && (
                  <div>
                    <Label
                      htmlFor="people"
                      className="text-sm text-muted-foreground"
                    >
                      People
                    </Label>
                    <PeopleSelector
                      mode="multi"
                      selectedPersonIds={formData.personIds}
                      onPersonIdsChange={(personIds) =>
                        setFormData({ ...formData, personIds })
                      }
                      placeholder="Select people"
                      size="sm"
                    />
                  </div>
                )}
                {showLocationField && (
                  <div
                    className={stageRequirements.getStandardFieldClassName(
                      "locationId",
                    )}
                    data-stage-field="locationId"
                  >
                    <Label
                      htmlFor="location"
                      className="text-sm text-muted-foreground"
                    >
                      Location
                      {stageRequirements.isStandardRequired("locationId") && (
                        <RequiredMark />
                      )}
                    </Label>
                    <LocationSelector
                      currentlySelectedId={formData.locationId}
                      onSelectId={(locationId: string | null) =>
                        setFormData({ ...formData, locationId })
                      }
                      placeholder={getFieldPlaceholder(
                        "Select a location",
                        stageRequirements.isStandardRequired("locationId"),
                      )}
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold mb-1">
                  {displayOpportunity?.name}
                </h1>
                <CompanyDisplay
                  companyName={displayOpportunity?.company?.name ?? null}
                />
                <SalesRepDisplay
                  salesRepUserId={displayOpportunity?.salesRepUserId ?? null}
                />
                <EstimatorsDisplay
                  estimatorUserIds={displayOpportunity?.estimatorUserIds ?? []}
                />
                <PeopleDisplay people={displayOpportunity?.people} />
                {showLocationField && (
                  <LocationDisplay
                    locationName={displayOpportunity?.location?.name ?? null}
                  />
                )}
              </>
            )}
          </div>
          {!isEditing && (
            <div className="flex shrink-0 gap-2">
              {opportunityId && (
                <CopyLinkButton
                  path={`/opportunities/${opportunityId}`}
                  label={displayOpportunity?.name ?? "Opportunity"}
                  ariaLabel="Copy link to opportunity"
                  size="icon-sm"
                />
              )}
              {!isReadOnly && (
                <>
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  {onUnarchive && isArchived && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            aria-label="Unarchive opportunity"
                            onClick={handleUnarchiveClick}
                            disabled={isArchiving}
                          >
                            {isArchiving ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <ArchiveRestore className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Unarchive</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {onArchive && !isArchived && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            aria-label="Archive opportunity"
                            onClick={handleArchiveClick}
                            disabled={isArchiving}
                          >
                            {isArchiving ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <Archive className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Archive</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {displayOpportunity?.archivedAt && (
          <div className="mt-4 flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
            <Archive className="h-5 w-5 shrink-0 text-warning" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-warning-foreground">
                This opportunity is archived
              </p>
              <p className="text-xs text-warning">
                Archived on{" "}
                {new Date(displayOpportunity.archivedAt).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </p>
            </div>
            {onUnarchive && !isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnarchiveClick}
                disabled={isArchiving}
                className="shrink-0 border-warning/30 text-warning-foreground hover:bg-warning/15"
              >
                {isArchiving ? (
                  <Loader className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <ArchiveRestore className="h-4 w-4 mr-1.5" />
                )}
                Unarchive
              </Button>
            )}
          </div>
        )}

        <div className="border-t border-b py-4">
          {isEditing ? (
            <div>
              <Label htmlFor="stage" className="text-sm text-muted-foreground">
                Stage
              </Label>
              <Select
                value={formData.stageId ?? ""}
                onValueChange={(stageId: string) => {
                  const stage = getStage(stageId);
                  if (!stage) {
                    return;
                  }
                  setFormData(withStageSelected(formData, stage));
                }}
              >
                <SelectTrigger id="stage">
                  <SelectValue placeholder="Select a stage" />
                </SelectTrigger>
                <SelectContent>
                  {activeStages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {stageRequirements.stage && (
                <StageReadiness
                  stageName={stageRequirements.stage.name}
                  fields={stageRequirements.fields}
                  onSelectField={handleSelectRequiredField}
                />
              )}
            </div>
          ) : (
            <OpportunityStageStepper
              stages={activeStages}
              currentStage={
                currentStageId ? getStage(currentStageId) : undefined
              }
            />
          )}
        </div>

        <div
          className={cn(
            "grid gap-6",
            isEditing
              ? "grid-cols-2"
              : showProbability
                ? "grid-cols-3"
                : "grid-cols-2",
          )}
        >
          <div
            className={cn(
              isEditing &&
                stageRequirements.getStandardFieldClassName("amount"),
            )}
            data-stage-field="amount"
          >
            <p className="text-sm text-muted-foreground mb-2">
              Value
              {isEditing && stageRequirements.isStandardRequired("amount") && (
                <RequiredMark />
              )}
            </p>
            {isEditing ? (
              <CurrencyInput
                value={formData.amount}
                onChange={(value) =>
                  setFormData({ ...formData, amount: value || "0.00" })
                }
                placeholder="0.00"
                prefix="$"
              />
            ) : (
              <p className="text-2xl font-bold">{formattedAmount}</p>
            )}
          </div>
          {(isEditing || showProbability) && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Probability</p>
              {isEditing ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={MIN_OPPORTUNITY_PROBABILITY}
                      max={MAX_OPPORTUNITY_PROBABILITY}
                      value={formData.probability}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          probability: Math.min(
                            MAX_OPPORTUNITY_PROBABILITY,
                            Math.max(
                              MIN_OPPORTUNITY_PROBABILITY,
                              parseInt(e.target.value, 10) || 0,
                            ),
                          ),
                        })
                      }
                      className="w-24"
                      disabled={!IN_PROGRESS_STATUSES.includes(formData.status)}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  {!IN_PROGRESS_STATUSES.includes(formData.status) && (
                    <p className="text-xs text-muted-foreground">
                      Auto-set by status
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-2xl font-bold tabular-nums">
                  {displayOpportunity?.probability}%
                </p>
              )}
            </div>
          )}
          {!isEditing && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Created</p>
              <p className="text-2xl font-bold">
                {displayOpportunity &&
                  format(displayOpportunity.createdAt, "M/d/yyyy")}
              </p>
            </div>
          )}
        </div>

        {mode === "edit" && opportunityId && (
          <OpportunityTimingBar opportunityId={opportunityId} />
        )}

        {displayOpportunity && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="pt-1">
            <TabsList
              className="grid h-auto min-h-8 w-full items-stretch"
              style={{
                gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))`,
              }}
            >
              <TabsTrigger
                value="tasks"
                className="whitespace-normal px-2 text-xs leading-4"
              >
                Work Log
              </TabsTrigger>
              {hasCustomFields && (
                <TabsTrigger
                  value={CUSTOM_FIELDS_TAB}
                  className="whitespace-normal px-2 text-xs leading-4"
                >
                  Custom Fields
                </TabsTrigger>
              )}
              <TabsTrigger
                value="work"
                className="whitespace-normal px-2 text-xs leading-4"
              >
                {demandForecastingEnabled
                  ? "Quotes, Notes & Forecast"
                  : "Quotes & Notes"}
              </TabsTrigger>
              <TabsTrigger
                value="activity"
                className="whitespace-normal px-2 text-xs leading-4"
              >
                Activity
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tasks" className="mt-4 space-y-5">
              <OpportunityThreadsSection
                opportunityId={displayOpportunity.id}
                primaryThreadId={displayOpportunity.primaryThreadId ?? null}
                compact
                onThreadOpen={handleThreadOpen}
              >
                <OpportunityTasksSection
                  opportunityId={displayOpportunity.id}
                  compact
                  onThreadOpen={handleThreadOpen}
                />
              </OpportunityThreadsSection>
            </TabsContent>

            {hasCustomFields && customFieldsSchema && (
              <TabsContent value={CUSTOM_FIELDS_TAB} className="mt-4 space-y-3">
                {customFieldsDirty && !isReadOnly && (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetCustomFields}
                      disabled={isSaving}
                      className="h-8"
                    >
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveWithToast}
                      disabled={isSaving}
                      className="h-8"
                    >
                      {isSaving ? (
                        <Loader className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Save
                    </Button>
                  </div>
                )}
                <CustomFieldsSection
                  schema={customFieldsSchema}
                  values={formData.customFields}
                  readOnly={isReadOnly}
                  layout="rows"
                  additionalRequiredNames={
                    stageRequirements.customRequiredNames
                  }
                  getFieldClassName={
                    isEditing
                      ? stageRequirements.getCustomFieldClassName
                      : undefined
                  }
                  onChange={(fieldName, value) =>
                    setFormData({
                      ...formData,
                      customFields: {
                        ...formData.customFields,
                        [fieldName]: value,
                      },
                    })
                  }
                />
              </TabsContent>
            )}

            <TabsContent value="work" className="mt-4 space-y-5">
              <OpportunityQuotesSection
                opportunityId={displayOpportunity.id}
                compact
                readOnly={isReadOnly}
              />
              <NotesSection
                entityType="OPPORTUNITY"
                entityId={displayOpportunity.id}
                compact
              />
              {demandForecastingEnabled && (
                <OpportunityDemandForecastSection
                  opportunityId={displayOpportunity.id}
                  compact
                  readOnly={isReadOnly}
                />
              )}
            </TabsContent>

            <TabsContent value="activity" className="mt-4">
              <OpportunityActivityLog opportunity={displayOpportunity} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    );
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-full md:w-1/2 max-w-none overflow-y-auto">
          {mode === "create" ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-2xl">
                  Create New Opportunity
                </SheetTitle>
                <SheetDescription>
                  Fill in the details to create a new opportunity.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 flex flex-col gap-6">
                <OpportunityEditorSheet
                  formData={formData}
                  onFormDataChange={setFormData}
                  mode={mode}
                  opportunityId={opportunityId}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                  customFieldsSchema={customFieldsSchema}
                  showLocationField={showLocationField}
                  onArchive={undefined}
                  isSaving={isSaving}
                  isArchiving={isArchiving}
                />
              </div>
            </>
          ) : (
            <div className="py-6">
              {fetchError ? (
                <div className="flex h-full w-full items-center justify-center p-6">
                  <div className="text-center text-destructive">
                    <p>{fetchError}</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => handleOpenChange(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              ) : isLoading ? (
                <div className="flex h-full w-full items-center justify-center p-6">
                  <Loader className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                renderDetailView()
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Opportunity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive "{storeOpportunity?.name}"? This
              will cancel any associated tasks. You can unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveConfirm}
              disabled={isArchiving}
            >
              {isArchiving && <Loader className="h-4 w-4 mr-2 animate-spin" />}
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <StandardAgentThreadView
        threadId={selectedThreadId}
        isOpen={selectedThreadId !== null}
        onClose={handleThreadClose}
        enableTaskDeepLink={enableThreadDeepLink}
      />
    </>
  );
};
